import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import dayjs from 'dayjs'
import { type TokenResult } from '@stripe/stripe-js'
import { AxiosError } from 'axios'
import { logger, numberFormat } from 'shared/lib'
import { showToast } from 'shared/ui'
import { jsonParse } from 'shared/lib/jsonParse'
import {
  type IParamsRegisterAnswers,
  type IParamsSignUpOrganization,
  type IParamsValidateMaxMind,
  type ReCaptchaStore,
} from 'entities/Auth'
import { AuthApi } from 'entities/Auth/api'
import type { IParamsSignUpAnswers } from 'entities/Auth/store/types'
import type {
  IParamsUpdateUsersProfilesUiSettings,
  IResponseUser,
  IResponseUsersProfilesUiSettings,
} from 'entities/Users/api/types'
import { BillingApi } from 'entities/Billing'
import { UsersApi } from 'entities/Users'
import { ChannelsProfile } from 'entities/Users/channels/profile'
import { IntegrationsApi } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IGtmAnswers, RegisterStepEnum } from 'pages/register/ui/Register/store/type'
import { StepSignUpStore } from 'pages/register/ui/Register/ui/StepSignUp'
import { Step4Store } from 'pages/register/ui/Register/ui/Step4/store/step4Store'
import { Step3Store } from 'pages/register/ui/Register/ui/Step3/store/step3Store'
import { Step2Store } from 'pages/register/ui/Register/ui/Step2/store/step2Store'
import { Step1Store } from 'pages/register/ui/Register/ui/Step1/store/step1Store'

import { GtmStore } from './gtmStore'

export class RegisterStore {
  private _step1Store = new Step1Store()
  private _step2Store = new Step2Store()
  private _step3Store = new Step3Store()
  private _step4Store = new Step4Store()
  private readonly _stepSignUpStore: StepSignUpStore
  private _gtmStore = new GtmStore()
  private _step = RegisterStepEnum.stepSignUp
  private _loading = false
  private _preloading = false
  private _inProgress = false
  private _profileChanelSubscribed = false
  private _profileId = 0

  private _multiOrganizationRegistration = false
  private _crmId = 0
  private _terms = true
  private _city = ''
  private _region = ''
  private _noCardTest = false
  private _timezone = ''
  private _newBilling = true

  // Infusionsoft
  private _leadSource = ''
  private _gaSource = ''
  private _gaMedium = ''
  private _gaTerm = ''
  private _gaContent = ''
  private _gaCampaign = ''
  private _gaReferurl = ''
  private _gaIp = ''

  // UTM
  private _utmCampaign = ''
  private _utmContent = ''
  private _utmMedium = ''
  private _utmSource = ''

  // STRIPE
  private _token = ''
  private _plan = ''

  // Handle completed profile with oid
  private _disposeHasOid: IReactionDisposer | null = null

  // Coupon
  private _disposeCoupon: IReactionDisposer | null = null
  private _couponTitle = ''

  constructor(private _entities: IEntitiesStore, private _storeReCaptchaStore: ReCaptchaStore) {
    this._stepSignUpStore = new StepSignUpStore(this, this._entities)
    this.init()
    this.reactionHasOid()
    this.reactionCoupon()

    makeAutoObservable(this)
  }

  get storeReCaptchaStore() {
    return this._storeReCaptchaStore
  }

  get couponTitle() {
    return this._couponTitle
  }

  get inProgress() {
    return this._inProgress
  }

  get preloading() {
    return this._preloading
  }

  get profileChanelSubscribed() {
    return this._profileChanelSubscribed
  }

  get stepSignUpStore() {
    return this._stepSignUpStore
  }

  get step1Store() {
    return this._step1Store
  }

  get step2Store() {
    return this._step2Store
  }

  get step3Store() {
    return this._step3Store
  }

  get step4Store() {
    return this._step4Store
  }

  get gtmStore() {
    return this._gtmStore
  }

  get step() {
    return this._step
  }

  get loading() {
    return this._loading
  }

  get emailStorageKey() {
    return 'signUpEmail'
  }

  get steps() {
    return [
      RegisterStepEnum.stepSignUp,
      RegisterStepEnum.step1,
      RegisterStepEnum.step2,
      RegisterStepEnum.step3,
      RegisterStepEnum.step4,
    ]
  }

  get sessionId() {
    return ''
  }

  get hasNextStep() {
    return this.step !== RegisterStepEnum.step4
  }

  get hasPrevStep() {
    return this.step !== RegisterStepEnum.stepSignUp
  }

  get progressPercent() {
    switch (this._step) {
      case RegisterStepEnum.stepSignUp:
        return 0
      case RegisterStepEnum.step1:
        return 20
      case RegisterStepEnum.step2:
        return 40
      case RegisterStepEnum.step3:
        return 60
      case RegisterStepEnum.step4:
        return 80
      default:
        return 0
    }
  }

  get coupon() {
    const searchParams = new URLSearchParams(window.location.search)
    return searchParams.get('c') || ''
  }

  get hasCoupon() {
    return !!this.coupon
  }

  get registerAnswerStep() {
    switch (this._step) {
      case RegisterStepEnum.stepSignUp:
        return '0'
      case RegisterStepEnum.step1:
        return '1'
      case RegisterStepEnum.step2:
        return '2'
      case RegisterStepEnum.step3:
        return '3'
      case RegisterStepEnum.step4:
        return '4'
      default:
        return '0'
    }
  }

  get numberVendorKey() {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.has('twilio')) return 'twilio-integration'
    return ''
  }

  get profileId() {
    return this._profileId
  }

  initSignUpEmail() {
    const email = localStorage.getItem(this.emailStorageKey)
    if (email) this.stepSignUpStore.setEmail(email)
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  setCouponTitle = (value: string) => {
    this._couponTitle = value
  }

  initStartStep = async () => {
    let startStep = RegisterStepEnum.stepSignUp

    if (this._entities.authStore.hasOid === false) {
      startStep = RegisterStepEnum.step1
      try {
        const { data } = await UsersApi.getUsersProfilesUiSettings('registration-steps')
        await this.initAnswers(data)
        if (!data) this.logGtmAnswers()

        if (this.step1Store.isCompletedStep) startStep = RegisterStepEnum.step2
        if (this.step2Store.isCompletedStep) startStep = RegisterStepEnum.step3
        if (this.step3Store.isCompletedStep) startStep = RegisterStepEnum.step4
      } catch (e) {
        logger.error(e)
      }
    }

    this._step = startStep
  }

  initAnswers = async (data: IResponseUsersProfilesUiSettings) => {
    this.step1Store.initAnswers(data)
    this.step2Store.initAnswers(data)
    this.step4Store.initAnswers(data)
    this.step3Store.initAnswers(data)
  }

  updateAnswers = async () => {
    const answers: IParamsSignUpAnswers = {
      email: this.stepSignUpStore.email,
      ...this._step1Store.payload,
      ...this._step2Store.payload,
      ...this._step3Store.payload,
      ...this._step4Store.payload,
    }

    const data: IParamsUpdateUsersProfilesUiSettings = {
      source_type: 'registration-steps',
      items: {
        answers,
      },
    }

    try {
      await UsersApi.updateUsersProfilesUiSettings(data)
    } catch (error) {
      logger.error(error)
    }
  }

  initProfile = async () => {
    if (!this._entities.authStore.hasToken) return

    try {
      const {
        data: { email, organization_in_progress, id },
      } = await UsersApi.getUsersProfile()

      this._profileId = id
      this._inProgress = organization_in_progress
      this.stepSignUpStore.setEmail(email)
      new ChannelsProfile(this._entities.websocket).subscribeChannels(id)
      this._profileChanelSubscribed = true
    } catch (error) {
      logger.error(error)
    }
  }

  initTwilio = async () => {
    const searchParams = new URLSearchParams(window.location.search)
    const installationSid = searchParams.get('installationSid')
    const accountSid = searchParams.get('accountSid')

    if (accountSid && installationSid) {
      const {
        data: {
          owner: { email, first_name, last_name },
        },
      } = await IntegrationsApi.getTwilioAccount({
        installation_sid: installationSid,
        account_sid: accountSid,
      })

      if (email) this.stepSignUpStore.setEmail(email)
      if (first_name) this.step4Store.setFirstName(first_name)
      if (last_name) this.step4Store.setLastName(last_name)
    }
  }

  init = () => {
    this._newBilling = window.Config?.new_billing_registration === '1'
    this._timezone = dayjs.tz.guess()

    const utmJson = sessionStorage.getItem('utm')
    const utmData = utmJson ? jsonParse<{ [key: string]: string } | null>(utmJson) : null
    this._utmCampaign = utmData?.utm_campaign || ''
    this.initSignUpEmail()
    this.initTwilio()
    this._stepSignUpStore.setHandleNextStep(this.handleNextStep)
    this._stepSignUpStore.init()
  }

  logAnswers = async (data?: IResponseUser) => {
    try {
      const payload: IParamsRegisterAnswers = {
        step: this.registerAnswerStep,
        email: this.stepSignUpStore.email,
        session_id: this.sessionId,
        profile_id: this.profileId,
        user_id: data?.id || 0,
        coupon_applied: this.hasCoupon ? 'yes' : 'no',
        ...this.step1Store.answers,
        ...this.step2Store.answers,
        ...this.step3Store.answers,
      }

      await AuthApi.logRegisterAnswers(payload)
    } catch (error) {
      logger.error(error)
    }
  }

  logGtmAnswers = async () => {
    try {
      const data: IGtmAnswers = {
        step: this.step,
        email: this.stepSignUpStore.email,
        userId: this.profileId,
        plan: this._plan,
        ...this.step1Store.gtmAnswers,
        ...this.step2Store.gtmAnswers,
        ...this.step3Store.gtmAnswers,
        ...this.step4Store.gtmAnswers,
      }

      this.gtmStore.setAnswers(data)
    } catch (error) {
      logger.error(error)
    }
  }

  handleNextStep = () => {
    if (!this.hasNextStep) {
      this.handleSignUp()
      return
    }

    this.logGtmAnswers()
    this.logAnswers()
    this.updateAnswers()

    const nextStepIdx = this.steps.indexOf(this.step) + 1
    this._step = this.steps[nextStepIdx]
  }

  reset = () => {
    this.stepSignUpStore.reset()
    this.step1Store.reset()
    this.step2Store.reset()
    this.step3Store.reset()
    this.step4Store.reset()
  }

  handleLogout = async () => {
    try {
      this._preloading = true
      await this._entities.authStore.logout()
      localStorage.setItem(this.emailStorageKey, this.stepSignUpStore.email)
    } catch (e) {
      logger.error(e)
    } finally {
      window.location.reload()
    }
  }

  handlePrevStep = () => {
    if (!this.hasPrevStep) return
    const prevStepIdx = this.steps.indexOf(this.step) - 1
    const prevStep = this.steps[prevStepIdx]

    if (prevStep === RegisterStepEnum.stepSignUp) this.handleLogout()

    this._step = prevStep
  }

  get stripePayload() {
    return {
      token: this._token,
      plan: this._plan,
      coupon: this.coupon,
    }
  }

  get utmPayload() {
    return {
      utm_campaign: this._utmCampaign,
      utm_content: this._utmContent,
      utm_medium: this._utmMedium,
      utm_source: this._utmSource,
    }
  }

  get infusionsoftPayload() {
    return {
      lead_source: this._leadSource,
      ga_source: this._gaSource,
      ga_medium: this._gaMedium,
      ga_term: this._gaTerm,
      ga_content: this._gaContent,
      ga_campaign: this._gaCampaign,
      ga_referurl: this._gaReferurl,
      ga_ip: this._gaIp,
    }
  }

  get maxMindPayload(): IParamsValidateMaxMind {
    const payload: IParamsValidateMaxMind = {
      email: this.stepSignUpStore.email,
      number: this.step4Store.formattedNumber,
      ...this._storeReCaptchaStore.params,
    }

    if (this.step4Store.smsCode) payload.sms_code = this.step4Store.smsCode

    return payload
  }

  get registerPayload(): IParamsSignUpOrganization {
    return {
      // Initial data
      multiorganization_registration: this._multiOrganizationRegistration,
      crm_id: this._crmId,
      terms: this._terms,
      city: this._city,
      region: this._region,
      noCardTest: this._noCardTest,
      timezone: this._timezone,
      new_billing: this._newBilling,
      number_vendor_key: this.numberVendorKey,
      email: this.stepSignUpStore.email,
      // Stripe
      ...this.stripePayload,
      // Infusionsoft
      ...this.infusionsoftPayload,
      // UTM
      ...this.utmPayload,
      // Step 1
      ...this.step1Store.payload,
      // Step 2
      ...this._step2Store.payload,
      // Step 3
      ...this.step3Store.payload,
      // Step 4
      ...this._step4Store.payload,

      ...this._storeReCaptchaStore.params,
    }
  }

  private _onSubmitCard: (() => Promise<TokenResult | undefined>) | null = null

  setOnSubmit = (onSubmit: () => Promise<TokenResult | undefined>) => {
    this._onSubmitCard = onSubmit
  }

  onAddCard = async () => {
    if (this._step4Store.cardError) return
    if (this._onSubmitCard) {
      try {
        const res = await this._onSubmitCard()
        if (!res || res?.error) {
          runInAction(() => {
            this._step4Store.setCardError(
              res?.error.message || 'Please provide another credit card'
            )
          })
          return
        }

        this._token = res.token.id
        this._step4Store.setCardError(null)
      } catch (e) {
        logger.error(e)
      }
    }
  }

  handleSignUp = async () => {
    try {
      this.updateAnswers()
    } catch (e) {
      logger.error(e)
    }

    try {
      this.setLoading(true)

      await this.onAddCard()

      await this._storeReCaptchaStore.setExecuteRecaptcha('sign_up_validate')
      await AuthApi.validateMaxMind(this.maxMindPayload, {
        reCaptchaStore: this._storeReCaptchaStore,
      })

      await this._storeReCaptchaStore.setExecuteRecaptcha('sign_up_organization_create')
      await this._entities.authStore.signUpOrganization(this.registerPayload, {
        reCaptchaStore: this._storeReCaptchaStore,
      })

      localStorage.removeItem(this.emailStorageKey)
      this.step4Store.closeMaxMindModal()
    } catch (e) {
      if (e instanceof Error) {
        if (e.message === 're_captcha_v2_hidden') {
          this.setLoading(false)
        }
      }

      if (e instanceof AxiosError) {
        logger.error(e)

        this.setLoading(false)

        if (e.response?.status === 500) this.step4Store.setShowRetryError(true)

        if (this._storeReCaptchaStore.checkServerError(e.errors.getError('message'))) return

        if (
          e.errors.getError('message') === 'code_required' ||
          e.errors.getError('message') === 'code_incorrect'
        ) {
          this.step4Store.triggerMaxMindModal(e.errors.getError('message') === 'code_incorrect')
          return
        }

        e.errors.entries().forEach(([key, msg]) => {
          if (key === 'number') {
            this.step4Store.phoneInputStore.setNumberRequestError(msg.value)
          } else {
            showToast({ type: 'error', title: msg.value })
          }
        })
      }
    }
  }

  handleCoupon = async () => {
    try {
      const { data } = await BillingApi.getCoupon(this.coupon)

      let discountText = ''
      let durationText = ''

      if (data.amount_off) {
        discountText +=
          data.currency === 'usd'
            ? numberFormat({
                value: data.amount_off / 100,
                minimumFractionDigits: 2,
                currency: 'USD',
              })
            : `${data.amount_off / 100} ${data.currency}`
      } else if (data.percent_off) {
        discountText += `${data.percent_off}%`
      }

      if (data.duration_in_months) {
        durationText +=
          data.duration_in_months > 1 ? ` for ${data.duration_in_months} months` : ' for 1 month'
      } else if (data.duration) {
        durationText += data.duration === 'once' ? ' the first month' : ` ${data.duration}`
      }

      this.setCouponTitle(`Coupon applied! Enjoy ${discountText} off ${durationText}.`)
    } catch (e) {
      logger.error(e)
    }
  }

  reactionCoupon = () => {
    this._disposeCoupon?.()
    this._disposeCoupon = reaction(
      () => this.hasCoupon,
      (hasCoupon) => {
        if (hasCoupon) this.handleCoupon()
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionHasOid = () => {
    this._disposeHasOid?.()
    this._disposeHasOid = reaction(
      () => this._entities.authStore.hasToken,
      async (hasToken) => {
        if (hasToken) {
          this._entities.websocket.connect()
          try {
            this._preloading = true
            await this.initProfile()
            await this.initStartStep()
          } catch (e) {
            logger.error(e)
          } finally {
            this._preloading = false
          }
        }
      },
      {
        fireImmediately: true,
      }
    )
  }
}
